import React, { memo, type ReactNode, useState } from 'react';
import { Chip, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CHIPS, CONFIG, KINDS, SIZES } from './constant';
import { getPercentage, pathForSemiCircularSvg } from './utils';

import './SodRiskScore.css';

interface SodRiskScoreProps {
  kind?: string;
  size?: string;
  value?: number;
  label?: string;
  chipKind?: string;
  className?: string;
  shouldShowZero?: boolean;
  customChip?: ReactNode;
}

function SodRiskScore({
  kind,
  size,
  value,
  label,
  chipKind,
  className,
  shouldShowZero,
  customChip,
}: SodRiskScoreProps) {
  const [svgDimensions] = useState(CONFIG[size ?? SIZES.small]);
  const [percentage] = useState(getPercentage(value, shouldShowZero));

  const progressStatsIconIndicator = cx(
    'Sod-RiskScore-progressStatsIconIndicator',
    `Sod-RiskScore-progressStatsIconIndicator--${kind ?? ''}`,
    className
  );

  const progressStatsNumberColor = cx(
    `Sod-RiskScore-progressStatsNumberColor--${kind ?? ''}`
  );

  const progressStatsLabelColor = cx(
    `Sod-RiskScore-progressLabel--${kind ?? ''}`
  );

  return (
    <div
      className={`Sod-RiskScore--${size ?? ''}${
        label?.trim()?.length === 0 && size === SIZES.regular ? '--noLabel' : ''
      }`}>
      <div className='Sod-RiskScore-wrapper'>
        <div className='Sod-RiskScore-progressStats'>
          <div className='Sod-RiskScore-progressStatsIcon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={svgDimensions.width}
              height={svgDimensions.height}
              viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
              fill='none'>
              <path
                d={pathForSemiCircularSvg(
                  parseInt(String(svgDimensions.width / 2), 10),
                  parseInt(String(svgDimensions.height / 2), 10),
                  parseInt(
                    String(svgDimensions.width / 2 - svgDimensions.strokeWidth),
                    10
                  ),
                  -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10),
                  parseInt(String(svgDimensions.baseSpanInDegree / 2), 10)
                )}
                fill='none'
                stroke='#EBECF2'
                strokeWidth={svgDimensions.strokeWidth}
                strokeLinecap='round'
              />
              {typeof percentage === 'number' && (
                <path
                  className={progressStatsIconIndicator}
                  d={pathForSemiCircularSvg(
                    parseInt(String(svgDimensions.width / 2), 10),
                    parseInt(String(svgDimensions.height / 2), 10),
                    parseInt(
                      String(
                        svgDimensions.width / 2 - svgDimensions.strokeWidth
                      ),
                      10
                    ),
                    -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10),
                    -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10) +
                      (percentage / 100) * svgDimensions.baseSpanInDegree
                  )}
                  fill='none'
                  stroke='#028329'
                  strokeWidth={svgDimensions.strokeWidth}
                  strokeLinecap='round'
                />
              )}
            </svg>
          </div>
          <div className='Sod-RiskScore-progressStatsNumber'>
            <Typography
              kind={size === SIZES.regular ? 'h2' : 'body2'}
              className={progressStatsNumberColor}>
              {percentage}
            </Typography>
          </div>
        </div>
        {size === SIZES.small && (
          <div className='Sod-RiskScore-progressChip--small'>
            <Typography kind='body3' className={progressStatsLabelColor}>
              {label}
            </Typography>
          </div>
        )}
        {!customChip &&
          size === SIZES.regular &&
          label &&
          label.trim().length > 0 && (
            <div className='Sod-RiskScore-progressChip--regular'>
              <Chip kind={chipKind} label={label} size='small' color={kind} />
            </div>
          )}
        {customChip}
      </div>
    </div>
  );
}

SodRiskScore.defaultProps = {
  kind: KINDS.info,
  size: SIZES.small,
  value: 0,
  label: 'None',
  chipKind: CHIPS.outlined,
  className: null,
  shouldShowZero: false,
  customChip: null,
};

export default memo(SodRiskScore);
